import type { DeliveryWindow } from "./domain";

export const BRANCH_TIME_ZONES = [
  {
    value: "Asia/Jakarta",
    shortLabel: "WIB",
    label: "WIB · Sumatra, Jawa, Kalimantan Barat/Tengah",
  },
  {
    value: "Asia/Makassar",
    shortLabel: "WITA",
    label:
      "WITA · Bali, NTB, NTT, Sulawesi, Kalimantan Selatan/Timur/Utara",
  },
  {
    value: "Asia/Jayapura",
    shortLabel: "WIT",
    label: "WIT · Maluku dan Papua",
  },
] as const;

export type BranchTimeZone = (typeof BRANCH_TIME_ZONES)[number]["value"];

export function normalizeBranchTimeZone(value: unknown): BranchTimeZone {
  return BRANCH_TIME_ZONES.some((zone) => zone.value === value)
    ? (value as BranchTimeZone)
    : "Asia/Makassar";
}

export function branchTimeZoneLabel(value: unknown): string {
  const zone = normalizeBranchTimeZone(value);
  return (
    BRANCH_TIME_ZONES.find((item) => item.value === zone)?.shortLabel ?? "WITA"
  );
}

function localParts(now: Date, timeZone: BranchTimeZone) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "00";
  return {
    year: Number(value("year")),
    month: Number(value("month")),
    day: Number(value("day")),
    hour: Number(value("hour")),
    minute: Number(value("minute")),
  };
}

function isoDate(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function nextCalendarDate(year: number, month: number, day: number): string {
  const tomorrow = new Date(Date.UTC(year, month - 1, day + 1));
  return isoDate(
    tomorrow.getUTCFullYear(),
    tomorrow.getUTCMonth() + 1,
    tomorrow.getUTCDate(),
  );
}

function minutes(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/u.exec(value);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour <= 23 && minute <= 59 ? hour * 60 + minute : null;
}

export interface DeliveryScheduleDecision {
  serviceDate: string;
  deferredToNextDay: boolean;
  cutoffPassed: boolean;
  timeZone: BranchTimeZone;
  timeZoneLabel: string;
}

export function evaluateDeliverySchedule(input: {
  now?: Date;
  timeZone?: string | null;
  windows: DeliveryWindow[];
  acceptOrdersOutsideHours: boolean;
}): DeliveryScheduleDecision {
  const now = input.now ?? new Date();
  const timeZone = normalizeBranchTimeZone(input.timeZone);
  const local = localParts(now, timeZone);
  const today = isoDate(local.year, local.month, local.day);
  const currentMinutes = local.hour * 60 + local.minute;
  const cutoffs = input.windows
    .map((window) => minutes(window.cutoffTime))
    .filter((cutoff): cutoff is number => cutoff !== null);
  const cutoffPassed =
    cutoffs.length > 0 && cutoffs.every((cutoff) => currentMinutes > cutoff);
  const deferredToNextDay = cutoffPassed && input.acceptOrdersOutsideHours;
  return {
    serviceDate: deferredToNextDay
      ? nextCalendarDate(local.year, local.month, local.day)
      : today,
    deferredToNextDay,
    cutoffPassed,
    timeZone,
    timeZoneLabel: branchTimeZoneLabel(timeZone),
  };
}
